import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@material-ui/core';
import { Form, Formik, FormikErrors } from 'formik';

import { useAuth } from '~/hooks/auth';
import { IState } from '~/store';
import { getCepRequest } from '~/store/modules/cep/actions';
import {
  addUserRequest,
  getUserByApplicationIdAndEmailRequest,
} from '~/store/modules/users/actions';

import formInitialValues from '../FormModel/formInitialValues';
import userFormModel from '../FormModel/userFormModel';
import validationSchema from '../FormModel/validationSchema';
import AddressForm from './AddressForm';
import CreateSuccess from './CreateSuccess';
import GeneralForm from './GeneralForm';
import ProfileForm from './ProfileForm';
import useStyles from './styles';

const steps = ['Geral', 'Endereço', 'Perfil'];
const { formId, formField } = userFormModel;

function renderStepContent(
  step: number,
  errors: FormikErrors<{
    [x: string]: string | never[];
  }>,
  mailError: string,
) {
  switch (step) {
    case 0:
      return (
        <GeneralForm
          errors={errors}
          formField={formField}
          mailError={mailError}
        />
      );
    case 1:
      return <AddressForm formField={formField} />;
    case 2:
      return <ProfileForm formField={formField} />;
    default:
      return <div>Not Found</div>;
  }
}

const IndexPage: React.FC = () => {
  const [initialValues, setInitialValues] = useState(formInitialValues);

  const { user } = useAuth();

  const loadingUser = useSelector((state: IState) => state.users.loading);
  const cep = useSelector((state: IState) => state.cep.cep);
  const groups = useSelector((state: IState) => state.users.groups);
  const profiles = useSelector((state: IState) => state.users.profiles);

  const [mailError, setMailError] = useState<string>('');

  const dispatch = useDispatch();

  const userByEmail = useSelector((state: IState) => state.users.user);

  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const currentValidationSchema = validationSchema[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const handleChange = useCallback((event, values) => {
    const { name, value } = event.target;

    if (name === 'zipCode' && !value) {
      setInitialValues({
        ...initialValues,
        zipCode: '',
        city: '',
        state: '',
        street: '',
        district: '',
      });
    }

    if (name === 'zipCode' && value.replace(/[^\d]+/g, '').length >= 8) {
      dispatch(getCepRequest(value.replace(/[^\d]+/g, '')));
      setInitialValues({
        ...values,
        [name]: value.replace(/[^\d]+/g, ''),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeEmail = useCallback((event: any) => {
    const { name, value } = event.target;

    if (name === 'email' && value) {
      dispatch(getUserByApplicationIdAndEmailRequest(12, event.target.value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userByEmail) {
      setMailError('email existente');
    } else {
      setMailError('');
    }
  }, [userByEmail]);

  useEffect(() => {
    if (cep) {
      setInitialValues({
        ...initialValues,
        zipCode: cep.cep,
        city: cep.city,
        state: cep.state,
        street: cep.street,
        district: cep.neighborhood,
      });
    } else {
      setInitialValues({
        ...initialValues,
        zipCode: '',
        city: '',
        state: '',
        street: '',
        district: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cep]);

  const handleBack = useCallback(() => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }, []);

  async function submitForm(values: any) {
    const selectedGroups = groups.filter((group) =>
      values.groups.includes(group.name),
    );
    const selectedProfiles = profiles.filter((profile) =>
      values.profiles.includes(profile.name),
    );

    const data = {
      ...values,
      cpf: values.cpf.replace(/[.-]+/g, ''),
      phone1: values.phone1.replace(/[ )(-]+/g, ''),
      phone2: values.phone2.replace(/[ )(-]+/g, ''),
      orgId: user.orgId,
      groups: selectedGroups,
      profiles: selectedProfiles,
    };

    dispatch(addUserRequest(data));
  }

  function handleSubmit(
    values: any,
    actions: { setTouched?: any; setSubmitting: any },
  ) {
    if (isLastStep) {
      submitForm(values);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  }

  return (
    <>
      <Typography component="h1" variant="h4" align="center">
        Cadastro de Usuário
      </Typography>
      <Stepper activeStep={activeStep} className={classes.stepper}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <>
        {activeStep === steps.length ? (
          <CreateSuccess />
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={currentValidationSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ values, errors }) => (
              <Form
                id={formId}
                onChange={(e: any) => {
                  handleChange(e, values);
                }}
                onBlur={(e: any) => {
                  handleChangeEmail(e);
                }}
              >
                {renderStepContent(activeStep, errors, mailError)}
                <div className={classes.buttons}>
                  {activeStep !== 0 && (
                    <Button onClick={handleBack} className={classes.button}>
                      Voltar
                    </Button>
                  )}
                  <div className={classes.wrapper}>
                    <Button
                      disabled={
                        (errors.email !== '' && mailError !== '') || loadingUser
                      }
                      type="submit"
                      variant="contained"
                      color="primary"
                      className={classes.button}
                    >
                      {isLastStep ? 'Enviar' : 'Próximo'}
                    </Button>
                    {loadingUser && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </>
    </>
  );
};

export default IndexPage;
